import React from 'react';
import { Input } from '@/components/ui/input';
import { ShieldOff, Check, Pencil } from 'lucide-react';
import { C, confidenceStyle } from './shared';

const YN = ['Yes', 'No'];
const selectTypes = ['single select', 'select', 'toggle', 'Y/N', 'Y/N + text', 'Y/N + currency', 'Y/N + lookup', 'band + number', 'single select + text',
  'Y/N + referral', 'Y/N + dates', 'select + number', 'Y/N + table'];
const numberTypes = ['currency', 'integer', 'number', 'year', 'repeatable'];

const optionsFor = q => {
  if (q.id === 'IC-01') return ['No', '1', '2', '3+'];
  if (q.id === 'RL-12') return ['Permanent', 'Volunteer', '8-20 km', 'Over 20 km'];
  if (q.answer_type.startsWith('Y/N') || q.answer_type === 'toggle') return YN;
  return q.options || [];
};

const Control = ({ q, value, onChange }) => {
  const base = 'h-8 rounded-sm border border-input bg-white px-2 text-xs w-full';
  if (q.answer_type === 'multi-select') {
    const cur = Array.isArray(value) ? value : [];
    return (
      <div className="flex flex-wrap gap-1" data-testid={`rq-field-${q.id}`}>
        {(q.options || []).map(o => (
          <button key={o} type="button" onClick={() => onChange(cur.includes(o) ? cur.filter(x => x !== o) : [...cur.filter(x => x !== 'None'), o])}
            className="text-[10px] px-1.5 py-0.5 rounded border" data-testid={`rq-field-${q.id}-${o.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
            style={cur.includes(o) ? { background: C.blue, color: 'white', borderColor: C.blue } : { borderColor: 'rgba(136,150,168,0.3)', color: C.navy }}>{o}</button>
        ))}
      </div>
    );
  }
  if (selectTypes.includes(q.answer_type)) {
    return (
      <select value={value ?? ''} onChange={e => onChange(e.target.value)} className={base} data-testid={`rq-field-${q.id}`}>
        <option value="">— choose —</option>
        {optionsFor(q).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (q.answer_type === 'date') return <Input type="date" value={value ?? ''} onChange={e => onChange(e.target.value)} className="h-8 rounded-sm text-xs" data-testid={`rq-field-${q.id}`} />;
  if (numberTypes.includes(q.answer_type)) return <Input type="number" value={value ?? ''} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} placeholder={q.id === 'RL-07' ? 'outbuildings sum insured $' : ''} className="h-8 rounded-sm text-xs" data-testid={`rq-field-${q.id}`} />;
  return <Input value={value ?? ''} onChange={e => onChange(e.target.value)} className="h-8 rounded-sm text-xs" data-testid={`rq-field-${q.id}`} />;
};

export const QuestionField = ({ q, answer, confirmed, onAnswer, onConfirm, onChangePrefill }) => {
  const pre = q.prefill && q.prefill.as === 'confirmation' ? q.prefill : null;
  const key = q.prefill && q.prefill.as === 'key' ? q.prefill : null;
  const origin = q.answer?.origin;
  const showInput = !key && (!pre || answer !== undefined || confirmed === false);
  return (
    <div className="py-1.5 border-t border-[rgba(136,150,168,0.1)]" data-testid={`rq-question-${q.id}`}>
      <div className="flex items-start justify-between gap-2">
        <label className="text-[11px] leading-snug" style={{ color: C.navy }}>
          <span className="font-mono text-[9px] mr-1" style={{ color: C.slate }}>{q.id}</span>{q.question}
          {q.required && <span style={{ color: C.red }}> *</span>}
        </label>
        <span className="flex items-center gap-1 shrink-0">
          {q.never_prefill && <span className="text-[9px] px-1 rounded flex items-center gap-0.5" style={{ background: 'rgba(226,75,74,0.08)', color: C.red }} title="security answers are never prefilled" data-testid={`rq-never-prefill-${q.id}`}><ShieldOff className="w-2.5 h-2.5" /> never prefilled</span>}
          <span className="text-[9px] px-1 rounded uppercase" data-testid={`rq-origin-${q.id}`}
            style={origin === 'typed' ? { background: 'rgba(26,76,124,0.12)', color: C.blue } : origin === 'confirmed' ? { background: 'rgba(29,158,117,0.12)', color: C.green } : { background: C.bg, color: C.slate }}>{origin}</span>
        </span>
      </div>
      {key && (
        <p className="mt-1 text-[11px] font-mono flex items-center gap-2" style={{ color: C.navy }} data-testid={`rq-key-${q.id}`}>{String(key.value)}<span className="text-[9px] font-sans" style={{ color: C.slate }}>{key.source} · the address is the key — counted as the one typed input</span></p>
      )}
      {pre && !showInput && (
        <div className="mt-1 flex items-center gap-2 flex-wrap" data-testid={`rq-prefill-${q.id}`}>
          <span className="text-[11px] font-mono" style={{ color: C.navy }}>{Array.isArray(pre.value) ? pre.value.join(', ') : String(pre.value)}</span>
          <span className="text-[9px] font-semibold px-1 py-0.5 rounded uppercase" style={confidenceStyle(pre.confidence)}>{pre.confidence}</span>
          <span className="text-[9px] line-clamp-1" style={{ color: C.slate }} title={pre.source}>{pre.source}</span>
          <button type="button" onClick={onConfirm} className="text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5" style={{ background: confirmed ? C.green : 'rgba(29,158,117,0.12)', color: confirmed ? 'white' : C.green }} data-testid={`rq-confirm-${q.id}`}><Check className="w-3 h-3" /> {confirmed ? 'confirmed' : 'confirm'}</button>
          <button type="button" onClick={onChangePrefill} className="text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5" style={{ background: C.bg, color: C.blue }} data-testid={`rq-change-${q.id}`}><Pencil className="w-3 h-3" /> change</button>
        </div>
      )}
      {showInput && <div className="mt-1"><Control q={q} value={answer ?? (q.default ?? '')} onChange={onAnswer} /></div>}
      {q.default && !pre && answer === undefined && <p className="text-[9px] mt-0.5" style={{ color: C.slate }}>default · not counted as a typed input until changed</p>}
    </div>
  );
};
